import axios from "axios";
import { buildApiUrl } from "./api";

// SAV-92A — read-only Engineering Lifecycle Preview client.
//
// This module talks ONLY to the browser-facing backend proxy route:
//   POST /api/engineering-lifecycle/preview
// It never talks to the internal Engineering Intelligence control plane
// (a private Cloud Run service) directly, and it never sends actor,
// authorization, commit, or execution metadata — the backend derives the
// requesting actor server-side from the authenticated session.
//
// This client performs a PREVIEW only. It has no authority to authorize,
// commit, dispatch, orchestrate, or execute anything, and it never will —
// that is enforced by the backend contract, not by this file.

const PREVIEW_PATH = "/engineering-lifecycle/preview";

export function buildLifecyclePreviewUrl() {
    return buildApiUrl(PREVIEW_PATH);
}

function isNonEmptyString(value) {
    return typeof value === "string" && value.trim().length > 0;
}

// SAV-92A functional remediation -- authoritative lifecycle decision source.
//
// A targeted re-review established that the authoritative Allowed/Blocked
// lifecycle decision lives at transition_decision.allowed (a boolean), with
// transition_decision.status as supplementary bounded detail.
// orchestration_status is a SEPARATE concept -- the handoff/orchestration
// routing outcome -- and is never the lifecycle authority. It defines exactly
// these six literal values and NO OTHERS:
//   blocked, proposed_handoff, terminal_state_no_handoff, ambiguous_handoff,
//   unroutable_handoff, evaluator_error
// Do not add values here without equivalent source evidence, and never derive
// the lifecycle Allowed/Blocked decision from any of them.
const RECOGNIZED_BLOCKED_ORCHESTRATION_STATUS = "blocked";

const RECOGNIZED_EVALUATOR_ERROR_ORCHESTRATION_STATUS = "evaluator_error";

// Proven, real, non-authority handoff outcomes -- rendered separately in the
// "Handoff preview" panel only when the lifecycle decision is Allowed. These
// never determine, and are never displayed as, the lifecycle decision itself.
// Exported so the UI layer maps exactly this vocabulary and nothing else.
export const ORCHESTRATION_HANDOFF_STATUS_LABELS = {
    proposed_handoff: "Proposed handoff",
    terminal_state_no_handoff: "Terminal state / no handoff",
    ambiguous_handoff: "Ambiguous handoff",
    unroutable_handoff: "Unroutable handoff",
};

const MAX_EVIDENCE_REASONS = 20;
const MAX_EVIDENCE_REASON_LENGTH = 200;

// SAV-92A functional remediation -- narrow, explicit evidence allowlist.
//
// The upstream response is passthrough-oriented with no enforced schema, so we
// never render it verbatim. We only ever read these specific keys:
// orchestration_status, contract_version, proposed_handoff_role, reasons
// (bounded orchestration/handoff evidence), plus transition_decision.allowed
// and transition_decision.status (the two lifecycle-decision fields an
// operator needs). We deliberately never read any other key of
// transition_decision -- guard results, authorization_result,
// proposed_audit_event, or anything not yet invented -- because that object is
// not narrowly scoped enough for safe operator display. Any other top-level
// field on the raw payload -- including a future stack_trace or
// service_account -- is ignored by construction, because this function never
// iterates the raw object's own keys.
function extractBoundedEvidence(raw) {
    if (!raw || typeof raw !== "object") return null;

    const evidence = {};

    if (isNonEmptyString(raw.orchestration_status)) {
        evidence.orchestrationStatus = raw.orchestration_status;
    }
    if (isNonEmptyString(raw.contract_version)) {
        evidence.contractVersion = raw.contract_version;
    }
    if (isNonEmptyString(raw.proposed_handoff_role)) {
        evidence.proposedHandoffRole = raw.proposed_handoff_role;
    }
    if (Array.isArray(raw.reasons)) {
        const reasons = raw.reasons
            .filter(isNonEmptyString)
            .slice(0, MAX_EVIDENCE_REASONS)
            .map((reason) => reason.slice(0, MAX_EVIDENCE_REASON_LENGTH));
        if (reasons.length) evidence.reasons = reasons;
    }

    const decision = raw.transition_decision;
    if (decision && typeof decision === "object" && !Array.isArray(decision)) {
        if (typeof decision.allowed === "boolean") {
            evidence.decisionAllowed = decision.allowed;
        }
        if (isNonEmptyString(decision.status)) {
            evidence.decisionStatus = decision.status;
        }
    }

    return Object.keys(evidence).length ? evidence : null;
}

// Only these three fields are legitimate for the browser to supply. Nothing
// else is ever added to the request body by this client.
export function validatePreviewRequest({ workItemKey, lifecycleInstanceId, event } = {}) {
    const missingFields = [];
    if (!isNonEmptyString(workItemKey)) missingFields.push("work_item_key");
    if (!isNonEmptyString(lifecycleInstanceId)) missingFields.push("lifecycle_instance_id");
    if (!isNonEmptyString(event)) missingFields.push("event");
    return missingFields;
}

function readSafeMessage(data) {
    return data && typeof data.message === "string" && data.message.trim() ? data.message : null;
}

function classifyPreviewSuccess(response) {
    const status = response?.status ?? null;
    const raw = response && typeof response.data === "object" && response.data !== null ? response.data : null;

    if (!raw) {
        return {
            kind: "malformed",
            status,
            message: "The preview response was not a JSON object.",
            raw: response ? response.data ?? null : null,
            evidence: null,
        };
    }

    const evidence = extractBoundedEvidence(raw);

    // orchestration_status is read only for the two failure/handoff signals it
    // legitimately carries (an internal evaluator error, and a contradiction
    // guard below). It is NEVER used to derive the lifecycle Allowed/Blocked
    // decision -- that comes exclusively from transition_decision.allowed.
    const orchestrationStatus = typeof raw.orchestration_status === "string" ? raw.orchestration_status : null;

    // An internal evaluator error means the backend could not safely evaluate
    // this transition at all. Fail closed regardless of what
    // transition_decision.allowed says -- a contradictory "allowed: true"
    // alongside evaluator_error must never surface as Allowed.
    if (orchestrationStatus === RECOGNIZED_EVALUATOR_ERROR_ORCHESTRATION_STATUS) {
        return {
            kind: "malformed",
            status,
            message: "The backend reported an internal evaluation error while previewing this transition.",
            raw,
            evidence,
        };
    }

    const decision = raw.transition_decision;
    const decisionIsObject = decision !== null && typeof decision === "object" && !Array.isArray(decision);
    const allowedValue = decisionIsObject ? decision.allowed : undefined;

    // The authoritative lifecycle decision comes exclusively from
    // transition_decision.allowed. Missing, malformed, non-boolean, or absent
    // transition_decision never infers Allowed -- HTTP 200 alone is never
    // sufficient, and no other field (including orchestration_status) can
    // substitute for this one.
    if (typeof allowedValue !== "boolean") {
        return {
            kind: "malformed",
            status,
            message: "The preview response did not include a valid lifecycle decision (transition_decision.allowed).",
            raw,
            evidence,
        };
    }

    if (allowedValue === true) {
        // Contradiction guard: an authoritative Allowed decision can never
        // coexist with a "blocked" orchestration_status. Rather than render a
        // misleading Allowed result from an internally inconsistent payload,
        // fail closed.
        if (orchestrationStatus === RECOGNIZED_BLOCKED_ORCHESTRATION_STATUS) {
            return {
                kind: "malformed",
                status,
                message: "The preview response contained contradictory lifecycle and orchestration fields.",
                raw,
                evidence,
            };
        }
        return { kind: "allowed", status, message: null, raw, evidence };
    }

    return { kind: "blocked", status, message: null, raw, evidence };
}

function classifyPreviewError(error) {
    if (!error?.response) {
        return {
            kind: "network",
            status: null,
            message: "Could not reach the Engineering Intelligence preview service.",
            raw: null,
            evidence: null,
        };
    }

    const { status, data } = error.response;
    const safeMessage = readSafeMessage(data);
    const raw = data || null;
    const evidence = extractBoundedEvidence(raw);

    if (status === 400) {
        return { kind: "invalid", status, message: safeMessage || "The preview request was invalid.", raw, evidence };
    }
    if (status === 401 || status === 403) {
        return { kind: "auth", status, message: safeMessage || "You are not authorized to preview this lifecycle transition.", raw, evidence };
    }
    if (status === 502 || status === 503) {
        return { kind: "unavailable", status, message: safeMessage || "Engineering Intelligence preview is currently unavailable.", raw, evidence };
    }
    return { kind: "unavailable", status, message: safeMessage || "Engineering Intelligence preview is currently unavailable.", raw, evidence };
}

// previewLifecycle sends EXACTLY {work_item_key, lifecycle_instance_id, event}
// to the backend proxy route. It never adds requested_by_actor_id,
// requested_by_actor_type, expected_state_version,
// canonical_work_item_evidence, authorization artifacts, commit metadata, or
// execution metadata -- the backend derives actor identity server-side and
// rejects any of those fields as unrecognized.
export async function previewLifecycle({ workItemKey, lifecycleInstanceId, event }) {
    const missingFields = validatePreviewRequest({ workItemKey, lifecycleInstanceId, event });
    if (missingFields.length > 0) {
        return {
            kind: "invalid",
            status: null,
            message: "The preview request is missing required fields.",
            missingFields,
            raw: null,
            evidence: null,
        };
    }

    const requestBody = {
        work_item_key: workItemKey.trim(),
        lifecycle_instance_id: lifecycleInstanceId.trim(),
        event: event.trim(),
    };

    let response;
    try {
        response = await axios.post(buildLifecyclePreviewUrl(), requestBody, { withCredentials: true });
    } catch (error) {
        return classifyPreviewError(error);
    }

    return classifyPreviewSuccess(response);
}
